import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'react-toastify';
import { getEventoById, getSessoesByEvento, criarInscricao, getMinhasInscricoes, getFullApiBaseUrl } from '../services/api';
import { Button } from '../components/ui/Button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/Card';
import { Skeleton } from '../components/ui/Skeleton';
import { Calendar, Clock, MapPin, Info, ListChecks, ArrowLeft } from 'lucide-react';
import Breadcrumb from '../components/Breadcrumb';

function EventoDetalhesPage() {
    const { eventoId } = useParams();
    const navigate = useNavigate();
    const { isAuthenticated, participanteId, participanteRole } = useAuth();

    const [evento, setEvento] = useState(null);
    const [sessoes, setSessoes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isSubscribed, setIsSubscribed] = useState(false);
    const [isSubscribing, setIsSubscribing] = useState(false);

    const fetchEventoData = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const eventoData = await getEventoById(eventoId);
            setEvento(eventoData);

            const sessoesData = await getSessoesByEvento(eventoId);
            setSessoes(sessoesData);

            if (isAuthenticated && participanteId) {
                const minhasInscricoes = await getMinhasInscricoes(participanteId);
                const subscribed = minhasInscricoes.some(inscricao => inscricao.evento.id === parseInt(eventoId));
                setIsSubscribed(subscribed);
            }
        } catch (err) {
            console.error("Erro ao buscar detalhes do evento:", err);
            setError(err.message || "Não foi possível carregar os detalhes do evento.");
            toast.error(err.message || "Erro ao carregar detalhes do evento.");
        } finally {
            setLoading(false);
        }
    }, [eventoId, isAuthenticated, participanteId]);

    useEffect(() => {
        fetchEventoData();
    }, [fetchEventoData]);

    const handleInscricao = async () => {
        if (!isAuthenticated) {
            toast.warn('Você precisa estar logado para se inscrever em um evento.');
            return;
        }
        if (participanteRole === 'ROLE_ADMIN') {
            toast.warn('Administradores não podem se inscrever em eventos.');
            return;
        }

        setIsSubscribing(true);
        try {
            await criarInscricao(eventoId);
            toast.success('Inscrição realizada com sucesso!');
            setIsSubscribed(true);
        } catch (err) {
            console.error('Erro ao inscrever-se:', err);
            toast.error(err.message || 'Ocorreu um erro ao tentar se inscrever.');
        } finally {
            setIsSubscribing(false);
        }
    };

    const formatDateTime = (dateTimeString) => {
        if (!dateTimeString) return 'N/A';
        const date = new Date(dateTimeString);
        return date.toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' });
    };

    const breadcrumbItems = [
        { label: 'Eventos', to: '/eventos' },
        { label: evento ? evento.nome : 'Detalhes do Evento' }
    ];

    if (loading) {
        return (
            <div className="container mx-auto p-4 md:p-6 lg:p-8">
                <Skeleton className="h-8 w-1/4 mb-4" />
                <Skeleton className="h-6 w-1/2 mb-6" />
                <Skeleton className="aspect-3508/1665 w-full mb-6 rounded-lg" /> {/* Usando aspect-3508/1665 */}
                <Card className="mb-6">
                    <CardHeader><Skeleton className="h-6 w-3/4 mb-2" /></CardHeader>
                    <CardContent className="space-y-3">
                        <Skeleton className="h-4 w-full" />
                        <Skeleton className="h-4 w-full" />
                        <Skeleton className="h-4 w-2/3" />
                        <Skeleton className="h-4 w-1/2" />
                    </CardContent>
                </Card>
                <Card>
                    <CardHeader><Skeleton className="h-6 w-1/3 mb-2" /></CardHeader>
                    <CardContent className="space-y-3">
                        {Array.from({ length: 3 }).map((_, i) => (
                            <Skeleton key={i} className="h-12 w-full" />
                        ))}
                    </CardContent>
                </Card>
            </div>
        );
    }

    if (error) {
        return <div className="container mx-auto p-4 text-destructive">Erro: {error}</div>;
    }

    if (!evento) {
        return <div className="container mx-auto p-4 text-muted-foreground">Evento não encontrado.</div>;
    }

    const isEventoAtivo = evento.ativo;
    const isEventoPassado = new Date(evento.dataFim) < new Date();
    
    // Função auxiliar para construir o URL completo da imagem
    const getFullImageUrl = (url) => {
        if (!url) return null;
        const baseUrl = getFullApiBaseUrl();
        
        // Remove barras iniciais do URL do evento
        let normalizedUrl = url.startsWith('/') ? url.substring(1) : url;
        
        // Adiciona 'files/' se não estiver presente
        if (!normalizedUrl.startsWith('files/')) {
            normalizedUrl = `files/${normalizedUrl}`;
        }
        
        // Concatena: http://localhost:8080/api/ + files/ + nome_do_arquivo
        return `${baseUrl}/${normalizedUrl}`;
    };

    // Constrói o URL completo da imagem
    const fullImageUrl = getFullImageUrl(evento.imagemUrl);
    
    // LOG DE VERIFICAÇÃO
    console.log("EventoDetalhesPage - Imagem URL:", fullImageUrl);

    return (
        <div className="container mx-auto p-4 md:p-6 lg:p-8">
            <Breadcrumb items={breadcrumbItems} />
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2 sm:gap-4 mb-6">
                <Button variant="outline" onClick={() => navigate('/eventos')} className="w-full sm:w-auto">
                    <ArrowLeft className="mr-2 h-4 w-4" /> Voltar aos Eventos
                </Button>
                <h1 className="text-3xl font-bold tracking-tight text-center sm:text-left w-full sm:w-auto">
                    {evento.nome}
                </h1>
            </div>

            {/* Imagem do Evento - Usando aspect-ratio e largura limitada */}
            {fullImageUrl && (
                <div className="mb-6 rounded-lg overflow-hidden shadow-lg w-full max-w-4xl mx-auto aspect-3508/1665"> {/* Usando aspect-3508/1665 */}
                    <img 
                        src={fullImageUrl} 
                        alt={`Imagem do evento ${evento.nome}`} 
                        className="w-full h-full object-cover object-top"
                    />
                </div>
            )}

            <Card className="mb-6">
                <CardHeader>
                    <CardTitle className="flex items-center mb-2"><Info className="mr-2 h-5 w-5" /> Detalhes do Evento</CardTitle>
                    {/* A CardDescription original foi mantida para o texto de descrição, mas o conteúdo HTML rico foi movido para um div separado na CardContent */}
                </CardHeader>
                <CardContent className="space-y-3 text-sm text-muted-foreground">
                    {/* Renderiza a descrição como HTML rico dentro de um DIV */}
                    <div className="leading-relaxed">
                        <div 
                            className="prose max-w-none text-muted-foreground" 
                            dangerouslySetInnerHTML={{ __html: evento.descricao }} 
                        />
                    </div>
                    
                    <div className="flex items-center">
                        <Calendar className="mr-2 h-4 w-4" />
                        <span>De {new Date(evento.dataInicio).toLocaleDateString()} a {new Date(evento.dataFim).toLocaleDateString()}</span>
                    </div>
                    <div className="flex items-center">
                        <MapPin className="mr-2 h-4 w-4" />
                        <span>Local: {evento.local}</span>
                    </div>
                    <div className="flex items-center">
                        <Clock className="mr-2 h-4 w-4" />
                        <span>Carga Horária Total: {evento.cargaHorariaTotal} horas</span>
                    </div>
                    <div className="flex items-center">
                        <span className={`px-2 py-1 text-xs font-semibold rounded-full ${isEventoAtivo ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                            {isEventoAtivo ? 'Ativo' : 'Inativo'}
                        </span>
                        {isEventoPassado && (
                            <span className="ml-2 px-2 py-1 text-xs font-semibold rounded-full bg-gray-100 text-gray-800">
                                Encerrado
                            </span>
                        )}
                    </div>
                </CardContent>
            </Card>

            <Card className="mb-6">
                <CardHeader>
                    <CardTitle className="flex items-center mb-2"><ListChecks className="mr-2 h-5 w-5" /> Sessões do Evento</CardTitle>
                    <CardDescription className="leading-relaxed">Horários e detalhes das sessões que compõem este evento.</CardDescription>
                </CardHeader>
                <CardContent>
                    {sessoes.length === 0 ? (
                        <p className="text-muted-foreground text-center py-4">Nenhuma sessão cadastrada para este evento.</p>
                    ) : (
                        <div className="space-y-4">
                            {sessoes.map(sessao => (
                                <div key={sessao.id} className="border p-4 rounded-md bg-muted/50">
                                    <h3 className="font-semibold text-lg mb-1">{sessao.nome}</h3>
                                    <p className="text-sm text-muted-foreground mb-2 leading-normal">{sessao.descricao}</p>
                                    <div className="flex items-center text-xs text-muted-foreground">
                                        <Calendar className="mr-1.5 h-3 w-3" />
                                        <span>Início: {formatDateTime(sessao.dataHoraInicio)}</span>
                                    </div>
                                    <div className="flex items-center text-xs text-muted-foreground">
                                        <Clock className="mr-1.5 h-3 w-3" />
                                        <span>Fim: {formatDateTime(sessao.dataHoraFim)}</span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </CardContent>
            </Card>

            {/* Botão de Inscrição */}
            {!isEventoPassado && isEventoAtivo && (
                <Card className="p-6 text-center">
                    {isAuthenticated ? (
                        participanteRole === 'ROLE_ADMIN' ? (
                            <p className="text-sm text-muted-foreground">Administradores não podem se inscrever em eventos.</p>
                        ) : isSubscribed ? (
                            <Button className="w-full bg-success text-success-foreground hover:bg-success/90" disabled>
                                Já Inscrito
                            </Button>
                        ) : (
                            <Button className="w-full" onClick={handleInscricao} disabled={isSubscribing}>
                                {isSubscribing ? 'A inscrever...' : 'Inscrever-se Neste Evento'}
                            </Button>
                        )
                    ) : (
                        <p className="text-sm text-muted-foreground">Faça login para se inscrever neste evento.</p>
                    )}
                </Card>
            )}
            {isEventoPassado && (
                <Card className="p-6 text-center">
                    <p className="text-muted-foreground">Este evento já foi encerrado.</p>
                </Card>
            )}
            {!isEventoAtivo && !isEventoPassado && (
                <Card className="p-6 text-center">
                    <p className="text-muted-foreground">As inscrições para este evento estão inativas no momento.</p>
                </Card>
            )}
        </div>
    );
}

export default EventoDetalhesPage;